import { Platform } from 'react-native';
import { colors, getThemeMode } from './colors';
import { typography } from './typography';
import { spacing } from './spacing';

// Lighter in light mode — a fixed black shadowColor at dark-mode opacity
// reads as a heavy, muddy smudge on light surfaces (RN shadows are alpha-
// blended onto whatever's behind them, so the same opacity looks very
// different depending on the surface). Resolved once at module-load, same
// boot-time-mode pattern as theme/colors.ts's buildShadows — the app already
// full-reloads on a theme toggle (see ThemeContext.tsx), so no live
// re-computation is needed here.
const isLightMode = getThemeMode() === 'light';

// ============================================================================
// DESIGN TOKENS
// ============================================================================

export const designTokens = {
  // Spacing Scale (8px base)
  spacing: {
    xs: 4,
    sm: 8,
    md: 16,
    lg: 24,
    xl: 32,
    '2xl': 48,
    '3xl': 64,
    '4xl': 96,
  },

  // Border Radius Scale — matches the imported --radius-* tokens
  borderRadius: {
    none: 0,
    sm: 8,
    md: 12,
    lg: 14,
    xl: 18,
    '2xl': 24,
    full: 9999,
  },

  // Shadows — shadowColor stays a fixed black regardless of theme (RN native
  // shadows are opacity-blended onto whatever's behind them, unlike CSS
  // box-shadow; using colors.text.primary here, which now resolves to near-
  // white, would make shadows invisible on the dark background). Opacity/
  // radius DO vary by mode — see isLightMode comment above.
  shadows: {
    none: {
      shadowColor: 'transparent',
      shadowOffset: { width: 0, height: 0 },
      shadowOpacity: 0,
      shadowRadius: 0,
      elevation: 0,
    },
    sm: {
      shadowColor: '#000000',
      shadowOffset: { width: 0, height: 2 },
      shadowOpacity: isLightMode ? 0.1 : 0.4,
      shadowRadius: isLightMode ? 4 : 6,
      elevation: 1,
    },
    md: {
      shadowColor: '#000000',
      shadowOffset: { width: 0, height: 10 },
      shadowOpacity: isLightMode ? 0.14 : 0.5,
      shadowRadius: isLightMode ? 20 : 30,
      elevation: 4,
    },
    lg: {
      shadowColor: '#000000',
      shadowOffset: { width: 0, height: 20 },
      shadowOpacity: isLightMode ? 0.18 : 0.6,
      shadowRadius: isLightMode ? 40 : 60,
      elevation: 8,
    },
    xl: {
      shadowColor: colors.brand.primary,
      shadowOffset: { width: 0, height: 8 },
      shadowOpacity: isLightMode ? 0.3 : 0.55,
      shadowRadius: 24,
      elevation: 12,
    },
  },

  // Component Sizes
  sizes: {
    // Input heights
    input: {
      sm: Platform.OS === 'web' ? 40 : 44,
      md: Platform.OS === 'web' ? 48 : 52,
      lg: Platform.OS === 'web' ? 56 : 60,
    },
    
    // Button heights
    button: {
      sm: Platform.OS === 'web' ? 32 : 36,
      md: Platform.OS === 'web' ? 40 : 44,
      lg: Platform.OS === 'web' ? 48 : 52,
      xl: Platform.OS === 'web' ? 56 : 60,
    },
    
    // Icon sizes
    icon: {
      xs: 12,
      sm: 16,
      md: 20,
      lg: 24,
      xl: 32,
      '2xl': 48,
    },
  },

  // Z-Index Scale
  zIndex: {
    base: 0,
    dropdown: 1000,
    sticky: 1020,
    fixed: 1030,
    modal: 1040,
    popover: 1050,
    tooltip: 1060,
    toast: 1070,
  },

  // Animation Durations
  animation: {
    fast: 150,
    normal: 250,
    slow: 350,
    slower: 500,
  },

  // Breakpoints for responsive design
  breakpoints: {
    mobile: 480,
    tablet: 768,
    desktop: 1024,
    wide: 1280,
  },
};

// ============================================================================
// COMPONENT STYLES
// ============================================================================

const buildComponentStyles = () => ({
  // Input Styles
  input: {
    base: {
      borderWidth: 1,
      borderColor: colors.border,
      borderRadius: designTokens.borderRadius.md,
      paddingHorizontal: designTokens.spacing.md,
      paddingVertical: designTokens.spacing.sm,
      fontSize: typography.fontSize.base,
      color: colors.textDark,
      backgroundColor: colors.background.surface,
      fontFamily: typography.fontFamily.primary,
      ...designTokens.shadows.sm,
    },
    states: {
      default: {
        borderColor: colors.border,
        backgroundColor: colors.background.surface,
      },
      focused: {
        borderColor: colors.primary,
        backgroundColor: colors.background.surface,
        ...designTokens.shadows.md,
      },
      error: {
        borderColor: colors.error,
        backgroundColor: colors.background.surface,
      },
      disabled: {
        borderColor: colors.border,
        backgroundColor: colors.lightGray,
        opacity: 0.6,
      },
    },
    sizes: {
      sm: {
        height: designTokens.sizes.input.sm,
        fontSize: typography.fontSize.sm,
        paddingHorizontal: designTokens.spacing.sm,
        paddingVertical: designTokens.spacing.xs,
      },
      md: {
        height: designTokens.sizes.input.md,
        fontSize: typography.fontSize.base,
        paddingHorizontal: designTokens.spacing.md,
        paddingVertical: designTokens.spacing.sm,
      },
      lg: {
        height: designTokens.sizes.input.lg,
        fontSize: typography.fontSize.lg,
        paddingHorizontal: designTokens.spacing.lg,
        paddingVertical: designTokens.spacing.md,
      },
    },
  },

  // Button Styles
  button: {
    base: {
      alignItems: 'center',
      justifyContent: 'center',
      borderRadius: designTokens.borderRadius.lg,
      flexDirection: 'row',
      position: 'relative',
      overflow: 'hidden',
      ...designTokens.shadows.md,
    },
    variants: {
      primary: {
        backgroundColor: colors.primary,
        borderWidth: 0,
        shadowColor: colors.primary,
        shadowOpacity: 0.3,
      },
      secondary: {
        backgroundColor: colors.secondary,
        borderWidth: 0,
        shadowColor: colors.secondary,
        shadowOpacity: 0.3,
      },
      accent: {
        backgroundColor: colors.accent,
        borderWidth: 0,
        shadowColor: colors.accent,
        shadowOpacity: 0.4,
      },
      outline: {
        backgroundColor: 'transparent',
        borderWidth: 2,
        borderColor: colors.primary,
        shadowColor: colors.primary,
        shadowOpacity: 0.2,
      },
      ghost: {
        backgroundColor: 'transparent',
        borderWidth: 0,
        ...designTokens.shadows.none,
      },
    },
    sizes: {
      sm: {
        paddingHorizontal: designTokens.spacing.md,
        paddingVertical: designTokens.spacing.sm,
        height: designTokens.sizes.button.sm,
        minHeight: designTokens.sizes.button.sm,
      },
      md: {
        paddingHorizontal: designTokens.spacing.lg,
        paddingVertical: designTokens.spacing.md,
        height: designTokens.sizes.button.md,
        minHeight: designTokens.sizes.button.md,
      },
      lg: {
        paddingHorizontal: designTokens.spacing.xl,
        paddingVertical: designTokens.spacing.lg,
        height: designTokens.sizes.button.lg,
        minHeight: designTokens.sizes.button.lg,
      },
      xl: {
        paddingHorizontal: designTokens.spacing['2xl'],
        paddingVertical: designTokens.spacing.xl,
        height: designTokens.sizes.button.xl,
        minHeight: designTokens.sizes.button.xl,
      },
    },
    states: {
      default: {
        opacity: 1,
      },
      pressed: {
        opacity: 0.8,
      },
      disabled: {
        opacity: 0.5,
        shadowOpacity: 0.1,
      },
    },
  },

  // Card Styles
  card: {
    base: {
      backgroundColor: colors.background.surface,
      borderRadius: designTokens.borderRadius.lg,
      padding: designTokens.spacing.lg,
      ...designTokens.shadows.md,
    },
    variants: {
      elevated: {
        ...designTokens.shadows.lg,
      },
      flat: {
        ...designTokens.shadows.none,
        borderWidth: 1,
        borderColor: colors.border,
      },
    },
  },

  // Container Styles
  container: {
    base: {
      flex: 1,
      backgroundColor: colors.background.primary,
    },
    variants: {
      padded: {
        paddingHorizontal: designTokens.spacing.lg,
        paddingVertical: designTokens.spacing.lg,
      },
      centered: {
        justifyContent: 'center',
        alignItems: 'center',
      },
    },
  },
});

// Built per theme mode, not once at import: these literals read
// colors.background.surface / colors.textDark etc., and a module-level
// snapshot kept whichever palette was active at boot — e.g. every Input
// stayed dark-surfaced after switching dark → light, because Input layers
// componentStyles.input over its own (correctly themed) styles. Consumers
// (Input/PasswordInput/Button) already re-render on theme change via
// useThemedStyles, so rebuilding on the next property access is enough.
let componentStylesCache: { mode: 'light' | 'dark'; value: ReturnType<typeof buildComponentStyles> } = {
  mode: getThemeMode(),
  value: buildComponentStyles(),
};

export const componentStyles = new Proxy({} as ReturnType<typeof buildComponentStyles>, {
  get(_target, key) {
    const mode = getThemeMode();
    if (componentStylesCache.mode !== mode) {
      componentStylesCache = { mode, value: buildComponentStyles() };
    }
    return (componentStylesCache.value as any)[key];
  },
});

// ============================================================================
// UTILITY FUNCTIONS
// ============================================================================

export const createComponentStyle = (
  baseStyle: any,
  variant?: string,
  size?: string,
  state?: string
) => {
  let style = { ...baseStyle };

  if (variant && baseStyle.variants?.[variant]) {
    style = { ...style, ...baseStyle.variants[variant] };
  }

  if (size && baseStyle.sizes?.[size]) {
    style = { ...style, ...baseStyle.sizes[size] };
  }

  if (state && baseStyle.states?.[state]) {
    style = { ...style, ...baseStyle.states[state] };
  }

  return style;
};

export const getResponsiveValue = (
  mobileValue: any,
  tabletValue: any,
  desktopValue: any,
  screenWidth: number
) => {
  if (screenWidth >= designTokens.breakpoints.desktop) {
    return desktopValue;
  } else if (screenWidth >= designTokens.breakpoints.tablet) {
    return tabletValue;
  }
  return mobileValue;
};
